import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Send, Mail, MapPin, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'
import { Button } from '@/components/ui/Button'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { contactSchema, type ContactFormData } from '@/lib/contact-schema'

// In production, use relative URL (empty string) since frontend is served by the same backend
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACBORBngf1r37neL'

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error'

export function Contact() {
  const { t } = useTranslation()
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>()

  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileInstance>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    // Validate Turnstile token
    if (!turnstileToken) {
      setTurnstileError(t('contact.form.turnstileError'))
      return
    }

    setSubmitStatus('submitting')
    setTurnstileError(null)

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          turnstileToken,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to send')
      }

      setSubmitStatus('success')
      reset()
      setTurnstileToken(null)
      turnstileRef.current?.reset()
    } catch (error) {
      console.error('Contact form error:', error)
      setSubmitStatus('error')
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-xl border bg-background text-foreground placeholder-foreground-muted transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 ${
      hasError ? 'border-error' : 'border-border hover:border-border-hover focus:border-primary'
    }`

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-20 lg:py-32 bg-background-secondary"
      aria-labelledby="contact-heading"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <span className="text-primary font-medium text-lg">{t('contact.subtitle')}</span>
            <h2 id="contact-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-6">
              {t('contact.title')}
            </h2>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              {t('contact.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Info */}
            <motion.div variants={itemVariants} className="space-y-8">
              <div className="bg-background rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold mb-6">{t('contact.letsConnect')}</h3>

                <div className="space-y-6">
                  <div className="flex items-center gap-4 hover:translate-x-1 transition-transform duration-200">
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">{t('contact.emailLabel')}</p>
                      <a
                        href="mailto:contact@fackelmeyer.com"
                        className="text-foreground hover:text-primary transition-colors"
                      >
                        contact@fackelmeyer.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 hover:translate-x-1 transition-transform duration-200">
                    <div className="p-3 bg-primary/10 text-primary rounded-xl">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-foreground-muted text-sm">{t('contact.location')}</p>
                      <p className="text-foreground">{t('contact.country')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="relative h-48 rounded-2xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent opacity-80" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white text-2xl font-bold text-center px-6">
                    {t('contact.openForProjects')}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={itemVariants}>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.form.name')} <span className="text-error">*</span>
                  </label>
                  <input
                    id="name"
                    type="text"
                    {...register('name')}
                    placeholder={t('contact.form.namePlaceholder')}
                    className={inputClasses(!!errors.name)}
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? 'name-error' : undefined}
                  />
                  {errors.name && (
                    <p id="name-error" className="mt-1.5 text-sm text-error">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.form.email')} <span className="text-error">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register('email')}
                    placeholder={t('contact.form.emailPlaceholder')}
                    className={inputClasses(!!errors.email)}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? 'email-error' : undefined}
                  />
                  {errors.email && (
                    <p id="email-error" className="mt-1.5 text-sm text-error">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.form.subject')}
                  </label>
                  <input
                    id="subject"
                    type="text"
                    {...register('subject')}
                    placeholder={t('contact.form.subjectPlaceholder')}
                    className={inputClasses(!!errors.subject)}
                  />
                  {errors.subject && (
                    <p className="mt-1.5 text-sm text-error">{errors.subject.message}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                    {t('contact.form.message')} <span className="text-error">*</span>
                  </label>
                  <textarea
                    id="message"
                    {...register('message')}
                    placeholder={t('contact.form.messagePlaceholder')}
                    rows={5}
                    className={inputClasses(!!errors.message)}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? 'message-error' : undefined}
                  />
                  {errors.message && (
                    <p id="message-error" className="mt-1.5 text-sm text-error">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                {/* Turnstile Captcha */}
                <div>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setTurnstileToken(token)
                      setTurnstileError(null)
                    }}
                    onError={() => {
                      setTurnstileToken(null)
                      setTurnstileError(t('contact.form.turnstileError'))
                    }}
                    onExpire={() => {
                      setTurnstileToken(null)
                    }}
                    options={{
                      theme: 'auto',
                    }}
                  />
                  {turnstileError && (
                    <p className="mt-1.5 text-sm text-error">{turnstileError}</p>
                  )}
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div
                    className="flex items-center gap-2 p-4 bg-success/10 text-success rounded-lg"
                    role="alert"
                  >
                    <CheckCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span>{t('contact.form.success')}</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div
                    className="flex items-center gap-2 p-4 bg-error/10 text-error rounded-lg"
                    role="alert"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                    <span>{t('contact.form.error')}</span>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting || submitStatus === 'submitting'}
                >
                  {isSubmitting || submitStatus === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" aria-hidden="true" />
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      {t('contact.form.submit')}
                      <Send className="w-5 h-5 ml-2" aria-hidden="true" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
